import AuthService from 'common/auth.service';
import * as htmlEntities from 'html-entities';
import { isEmpty } from 'lodash';
import * as moment from 'moment';
import React from 'react';
import { api } from './axios';
import {
  API_BASE_URL,
  INCLUDES_NUMBER,
  INCLUDES_SYMBOL,
  INCLUDES_UPPERCASE,
  PermissionEntityEnum_SMK,
  PermissionEntityEnum_TPZ,
} from './constants';

const { htmlToText } = require('html-to-text');

export const titleCaseIfExists = (inputString) => {
  if (!inputString) {
    return '';
  }
  const str = inputString.toLowerCase().split(/[ -,_]/g);
  for (let i = 0; i < str.length; i += 1) {
    str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
  }
  return str.join(' ').trim();
};

export const isStringEquals = (s1, s2) => {
  return String(s1) === String(s2);
};

export const isStringEqualsIgnoreCase = (s1, s2) => {
  return s1 && s2 && String(s1).toLowerCase() === String(s2).toLowerCase();
};

export const formatDate = (date, format) => {
  return moment(date).format(format);
};

export const printDate = (date) => {
  return date ? formatDate(date, 'MMM DD, YYYY') : '';
};
export const printDateTime = (date) => {
  return date ? formatDate(date, 'MMM DD, YYYY - hh:mm a') : '';
};

export const printTime = (date) => {
  return date ? formatDate(date, 'hh:mm a') : '';
};
export const printDateTimeTwoLines = (date) => {
  return (
    <>
      {date && (
        <>
          {printDate(date)}
          <br />
          {printTime(date)}
        </>
      )}
    </>
  );
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const getOfferingFullDetails = (id, allOfferings) => {
  if (!id) {
    return [];
  }
  const subject = allOfferings.find((o) => o.id === id);

  const array = [subject];

  let parent = subject?.parentOffering;
  while (parent) {
    // eslint-disable-next-line no-loop-func
    const offer = allOfferings.find((o) => o.id === parent.id);
    array.push(offer);
    parent = offer.parentOffering;
  }

  return array.sort((a, b) => a.level - b.level);
};

export const getOfferingFullName = (offerings) => {
  return offerings
    .filter((o) => o.level > 0)
    .map((o) => o.displayName)
    .join(' | ');
};

export const getOfferingFullNamewithArrow = (offerings) => {
  return offerings
    .filter((o) => o.level > 0)
    .map((o) => o.displayName)
    .join(' --> ');
};

export const getNameInitials = (string) => {
  const names = string.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export const getFullName = (contactDetail) => {
  return contactDetail ? `${contactDetail?.firstName} ${contactDetail?.lastName}` : '';
};
export const printPhoneNumber = (phoneNumber) => {
  return (
    <>
      +{phoneNumber.countryCode}-{phoneNumber.number}
    </>
  );
};
export const printCurrency = (number) => {
  return new Intl.NumberFormat('en-IN', { currency: 'INR' }).format(number);
};

export const startOfDay = (date) => {
  return `${moment(date).format('YYYY-MM-DDT00:00:00')}Z`;
};

export const endOfDay = (date) => {
  return `${moment(date).format('YYYY-MM-DDT23:59:59')}Z`;
};

export const passwordPolicy = (pass) => {
  const alphaRegex = /\w+/;
  const numericRegex = /\d+/;
  return pass.length >= 8 && numericRegex.test(pass) && alphaRegex.test(pass);
};

export const urlSlugify = (input, length = -1) => {
  let text = input;
  if (text && text.length > 0) {
    text = text.replace(/'/g, '');
    text = text.replace(/[`~!@#$%^&*()_\-+=\[\]{};:‘’।'"\\|\/,.<>?\s]/g, ' ');
    text = text.replace(/^\s+|\s+$/gm, '');
    // text = text.replace(/\s+/g, "-");
    text = text
      .trim()
      .split(' ')
      .splice(0, length > 0 ? length : 100)
      .map((item) => {
        return item.trim();
      })
      .filter((item) => item.length > 0)
      .join('-');
    text = text.trim().toLowerCase();
  }
  return text;
};

export const cleanHTML = (html) => {
  let html1 = html;
  if (html && html.length > 0) {
    html1 = html.replace(/\\"/g, '"');
    html1 = html.replace(/style="[^\\"]*"/gi, '');
  }
  return html1;
};

export const convertHtmlToText = (html, removeNewLine = true) => {
  // let text = htmlToText(htmlEntities.decode(html), { preserveNewlines: false });
  let text = htmlToText(html, { preserveNewlines: false });
  text = text.replace(/"/g, '\\"');
  if (removeNewLine) {
    text = text.replace(/\n/g, ' ');
  }
  return text;
};

export const htmlDecode = (html) => {
  let text = '';
  try {
    text = cleanHTML(htmlEntities.decode(html));
  } catch (e) {
    text = '';
  }
  return text.replace(/(\r\n|\n|\r)/gm, '');
};

export const htmlEncode = (html) => {
  let text = '';
  try {
    text = cleanHTML(htmlEntities.encode(html));
  } catch (e) {
    text = '';
  }

  return text;
};

export const buildURL = (rawPath, pathParams = {}, queryParams = {}) => {
  let path = rawPath;
  Object.keys(pathParams).forEach((key) => {
    const t = pathParams[key];
    path = path?.replace(`:${key}`, t);
  });
  Object.keys(queryParams)
    .filter((key) => queryParams[key] !== null && typeof queryParams[key] !== 'undefined')
    .forEach((key, index) => {
      const param = encodeURIComponent(queryParams[key]);

      if (index === 0) {
        path += `?${key}=${param}`;
      } else {
        path += `&${key}=${param}`;
      }
    });
  return path;
};

export const isValidPassword = (val) =>
  val && val.length >= 8 && INCLUDES_NUMBER.test(val) && INCLUDES_SYMBOL.test(val) && INCLUDES_UPPERCASE.test(val);

export const getPermissions = (roles) => {
  // create entity type level mapping
  const auth = new AuthService();
  const source = auth.getAppSourceCookie();
  const permission_enum = source === 'schoolmykids' ? PermissionEntityEnum_SMK : PermissionEntityEnum_TPZ;

  const permissions = Object.values(permission_enum).reduce((acc, p) => {
    acc[p] = {
      entity: p,
      admin: false,
      import: false,
      export: false,
      reporting: false,
      publish: false,

      create: false,
      read: false,
      update: false,
      delete: false,
    };
    return acc;
  }, {});

  roles?.forEach((role) => {
    role.permissions?.forEach((p) => {
      const { entity, ...rest } = p;
      // Check if entity exists in PermissionEntityEnum
      if (permissions[entity]) {
        Object.entries(rest).forEach(([key, value]) => {
          permissions[entity][key] = permissions[entity][key] || value;
        });
      } else {
        console.warn(`Entity ${entity} does not exist in PermissionEntityEnum`);
      }
    });
  });

  return permissions;
};

export const checkIfValueExist = (value) => (value === null || value === ' ' ? undefined : value);

export const printSerialNumbers = (paginator) => {
  const { totalCount, size, currentPage } = paginator;
  const start = (currentPage - 1) * size + 1;
  const end = Math.min(currentPage * size, totalCount);

  const s_no = Array.from({ length: end - start + 1 }, (_, index) => start + index);
  return s_no;
};

export const stringInject = (str, data) => {
  if (typeof str === 'string' && data instanceof Array) {
    return str.replace(/({\d})/g, function(i) {
      return data[i.replace(/{/, '').replace(/}/, '')];
    });
  }
  if (typeof str === 'string' && data instanceof Object) {
    if (Object.keys(data).length === 0) {
      return str;
    }

    for (const key in data) {
      return str.replace(/({([^}]+)})/g, function(i) {
        const key = i.replace(/{/, '').replace(/}/, '');
        if (!data[key]) {
          return i;
        }

        return data[key];
      });
    }
  } else if (
    (typeof str === 'string' && data instanceof Array === false) ||
    (typeof str === 'string' && data instanceof Object === false)
  ) {
    return str;
  } else {
    return '';
  }
  return '';
};

// upload Images

export const showImage = (url) => {
  const { stgURL, prodURL } = getCurrentUserData();

  if (url?.startsWith(prodURL)) {
    return url;
  }
  const imageUrl = (url?.indexOf('/') === 0 ? '' : '/') + url;
  return stgURL + imageUrl;
};

export const handleUploadImage = (acceptedFile, setUploadImagePreview, setImageUploading) => {
  setImageUploading(true);
  const formData = new FormData();
  formData.append('file', acceptedFile);

  api({
    method: 'POST',
    url: `${API_BASE_URL}/attachment/upload`,
    data: formData,
  })
    ?.then((fileResponse) => {
      setUploadImagePreview(fileResponse);
      setImageUploading(false);
    })
    ?.catch((err) => {
      console.warn('error', err);
      setImageUploading(false);
    });
};

export const handleImageUploaded = async (uploadedImagePreview, acceptFile, forType, isAsset = false) => {
  if (!isEmpty(uploadedImagePreview)) {
    return await api({
      method: 'POST',
      url: `${API_BASE_URL}/${isAsset ? 'asset/admin' : 'attachment'}`,
      data: {
        ...uploadedImagePreview,
        forType,
      },
    })
      ?.then((file) => {
        return !isEmpty(acceptFile) ? (acceptFile == 'acceptID' ? file?.id : file) : file?.filename;
      })
      ?.catch((err) => {
        console.warn('err', err);
      });
  }
};

export function printNumber(input) {
  if (!input) {
    return input;
  }
  return input > 0 ? input?.toLocaleString('en-us') : '-';
}

export const printCurrencyTooltip = () => {
  return {
    formatter: (value) => {
      return printNumber(value);
    },
  };
};

export function printCurrencyFormat(value) {
  const billion = 1000000000;
  const million = 1000000;
  const thousand = 1000;
  const number = Number(value);

  if (isNaN(value) || !value) {
    return '-';
  } else if (number >= billion) {
    return (number % billion === 0 ? number / billion : (number / billion).toFixed(2)) + 'B';
  } else if (number >= million) {
    return (number % million === 0 ? number / million : (number / million).toFixed(2)) + 'M';
  } else if (number >= thousand) {
    return (number % thousand === 0 ? number / thousand : (number / thousand).toFixed(2)) + 'K';
  } else {
    return Number.isInteger(number) ? number?.toString() : number?.toFixed(2);
  }
}

export const printLabelsInCurrencyFormat = (props) => {
  return {
    formatter: (value) => {
      return printCurrencyFormat(value);
    },
    ...props,
  };
};

export const handleSaveAsset = async (file, type) => {
  if (!isEmpty(file)) {
    return await api({
      method: 'POST',
      url: `${API_BASE_URL}/asset/admin`,
      data: {
        ...file,
        forType: type,
      },
    })
      ?.then((file) => {
        return file;
      })
      ?.catch((err) => {
        console.warn('err', err);
      });
  }
};

export const handleSaveAttachement = async (file, type) => {
  if (!isEmpty(file)) {
    return await api({
      method: 'POST',
      url: `${API_BASE_URL}/attachment`,
      data: {
        ...file,
        forType: type,
      },
    })
      ?.then((file) => {
        return file;
      })
      ?.catch((err) => {
        console.warn('err', err);
      });
  }
};

export const getTranslationLabel = (obj) => {
  if (!obj) {
    return '';
  }

  return obj?.title || obj?.name || obj?.slug || '';
};

export const IMAGE_SIZES = {
  SMALL: 'thumbnailSmall',
  MEDIUM: 'thumbnailMedium',
  LARGE: 'thumbnailLarge',
  ORIGINAL: 'original',
};

export const getImageUrl = (attachmentObj, size) => {
  if (!attachmentObj) {
    return '';
  }
  const { stgURL, prodURL } = getCurrentUserData();

  if (attachmentObj[size]?.startsWith(prodURL)) {
    return attachmentObj[size];
  }

  const sizesOrder = Object.values(IMAGE_SIZES);

  if (!isEmpty(size)) {
    sizesOrder?.unshift(size);
  }

  for (let s of sizesOrder) {
    if (attachmentObj[s]?.startsWith(process.env.smkWebSiteDomain)) {
      return attachmentObj[s];
    }
    if (attachmentObj[s]) {
      const imageUrl = attachmentObj[s].startsWith('/') ? attachmentObj[s] : '/' + attachmentObj[s];
      return `${stgURL}${imageUrl}`;
    }
  }
};

export const getCurrentUserData = (appSource) => {
  const auth = new AuthService();
  const source = !isEmpty(appSource) ? appSource : auth.getAppSourceCookie();

  const sourceMap = {
    schoolmykids: {
      apiKey: process.env.REACT_APP_SCHOOLMYKIDS_API_KEY,
      apiURL: process.env.REACT_APP_SCHOOLMYKIDS_API_URL,
      stgURL: process.env.REACT_APP_SCHOOLMYKIDS_WEB_URL,
      prodURL: process.env.REACT_APP_SCHOOLMYKIDS_WEB_URL,
    },
    theparentz_com: {
      apiKey: process.env.REACT_APP_THEPARENTZ_COM_API_KEY,
      apiURL: process.env.REACT_APP_THEPARENTZ_COM_API_URL,
      stgURL: process.env.REACT_APP_THEPARENTZ_COM_WEB_URL,
      prodURL: process.env.REACT_APP_THEPARENTZ_COM_WEB_URL,
    },
    theparentz_ai: {
      apiKey: process.env.REACT_APP_THEPARENTZ_API_KEY,
      apiURL: process.env.REACT_APP_THEPARENTZ_API_URL,
      stgURL: process.env.REACT_APP_THEPARENTZ_WEB_URL,
      prodURL: process.env.REACT_APP_THEPARENTZ_WEB_URL,
    },
    theparentz: {
      apiKey: process.env.REACT_APP_THEPARENTZ_API_KEY,
      apiURL: process.env.REACT_APP_THEPARENTZ_API_URL,
      stgURL: process.env.REACT_APP_THEPARENTZ_WEB_URL,
      prodURL: process.env.REACT_APP_THEPARENTZ_WEB_URL,
    },
    mediwatcher: {
      apiKey: process.env.REACT_APP_MEDIWATCHER_API_KEY,
      apiURL: process.env.REACT_APP_MEDIWATCHER_API_URL,
      stgURL: process.env.REACT_APP_MEDIWATCHER_WEB_URL,
      prodURL: process.env.REACT_APP_MEDIWATCHER_WEB_URL,
    },
    periodtracker: {
      apiKey: process.env.REACT_APP_PERIOD_TRACKER_API_KEY,
      apiURL: process.env.REACT_APP_PERIOD_TRACKER_API_URL,
      stgURL: process.env.REACT_APP_PERIOD_TRACKER_WEB_URL,
      prodURL: process.env.REACT_APP_PERIOD_TRACKER_WEB_URL,
    },
    hr: {
      apiKey: process.env.REACT_APP_RHA_API_KEY,
      apiURL: process.env.REACT_APP_RHA_API_URL,
      stgURL: process.env.REACT_APP_RHA_WEB_URL,
      prodURL: process.env.REACT_APP_RHA_WEB_URL,
    },
  };

  return (
    sourceMap[source] || {
      apiKey: '',
      apiURL: '',
      stgURL: '',
      s3CloudfrontURL: '',
      prodURL: '',
    }
  );
};

export const formatInputDate = (date) => {
  if (moment?.isMoment(date)) {
    return date?.format('YYYY-MM-DD');
  }

  const momentDate = moment(date);

  if (momentDate.isValid()) {
    return momentDate.format('YYYY-MM-DD');
  }

  return null;
};

export const graphqlRemoveTypeNameKey = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(graphqlRemoveTypeNameKey);
  } else if (obj !== null && typeof obj === 'object') {
    const newObj = {};
    for (const key in obj) {
      if (key !== '__typename') {
        newObj[key] = graphqlRemoveTypeNameKey(obj[key]);
      }
    }
    return newObj;
  }
  return obj;
};

export const generateRandomImageName = (extension = 'jpg') => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 16; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return `${result}.${extension}`;
};

export const changeDecimalToMonths = (decimal) => {
  if (decimal <= 0) return '0 years 0 months';

  let years = Math.floor(decimal);
  let months = Math.round((decimal - years) * 12);

  if (months === 12) {
    months = 0;
    years += 1;
  }

  const yearText = years === 1 ? 'year' : 'years';
  const monthText = months === 1 ? 'month' : 'months';

  return `${years} ${yearText} ${months} ${monthText}`;
};

export const getFormattedDate = (date) => {
  if (isEmpty(date)) return '';
  const momentDateObj = moment(date);
  return moment().diff(momentDateObj, 'months') >= 1 ? momentDateObj.format('DD MMM YYYY') : momentDateObj.fromNow();
};

export const convertToDate = (relativeTime) => {
  if (!relativeTime || typeof relativeTime !== 'string') return '';

  const regex = /(\d+|a) (\w+) ago/;
  const match = relativeTime.match(regex);

  if (!match) return '';

  let amount = match[1] === 'a' ? 1 : parseInt(match[1], 10);
  let unit = match[2].replace(/s$/, '');

  return moment()
    .subtract(amount, unit)
    .format('YYYY-MM-DD HH:mm:ss');
};

export const customHandleSaveAsset = async (file, type, headers) => {
  if (!isEmpty(file)) {
    return await api(
      {
        method: 'POST',
        url: `${API_BASE_URL}/asset/admin`,
        data: {
          ...file,
          forType: type,
        },
      },
      headers
    )
      ?.then((file) => {
        return file;
      })
      ?.catch((err) => {
        console.warn('err', err);
      });
  }
};

export const customHandleSaveAttachement = async (file, type, headers) => {
  if (!isEmpty(file)) {
    return await api(
      {
        method: 'POST',
        url: `${API_BASE_URL}/attachment`,
        data: {
          ...file,
          forType: type,
        },
      },
      headers
    )
      ?.then((file) => {
        return file;
      })
      ?.catch((err) => {
        console.warn('err', err);
      });
  }
};

export const printBirthDifference = (dob) => {
  if (!dob || !moment(dob).isValid()) {
    console.log('Invalid date of birth provided.');
    return;
  }

  const birthDate = moment(dob);
  const now = moment();

  const years = now.diff(birthDate, 'years');
  birthDate.add(years, 'years');

  const months = now.diff(birthDate, 'months');
  birthDate.add(months, 'months');

  const days = now.diff(birthDate, 'days');

  let result = '';

  if (years > 0) {
    result += ` ${years} year${years !== 1 ? 's' : ''}`;
  }
  if (months > 0) {
    result += ` ${months} month${months !== 1 ? 's' : ''}`;
  }
  if (days > 0) {
    result += ` ${days} day${days !== 1 ? 's' : ''}`;
  }

  return result;
};

export const createPostDetailsLink = (data) => {
  const { stgURL } = getCurrentUserData();

  let post = !isEmpty(data?.post) ? data?.post : data;
  const postSlug = post?.slug?.length > 50 ? post?.slug?.slice(0, 50) : post?.slug;
  return (stgURL || process.env.REACT_APP_THEPARENTZ_WEB_URL) + `/community/post/${postSlug}-c203${post?.uid}`;
};

export const createGroupDetailsLink = (data) => {
  const { stgURL } = getCurrentUserData();

  let group = !isEmpty(data?.group) ? data?.group : data;
  return (stgURL || process.env.REACT_APP_THEPARENTZ_WEB_URL) + `/community/group/${group?.slug}`;
};

export function formatMonthsToYears(months) {
  if (!months || months <= 0) return '-';

  if (months < 19) {
    return `${months} month${months > 1 ? 's' : ''}`;
  }

  const years = Math.floor(months / 12);
  const remaining = months % 12;

  const yearPart = years > 0 ? `${years} year${years > 1 ? 's' : ''}` : '';
  const monthPart = remaining > 0 ? `${remaining} month${remaining > 1 ? 's' : ''}` : '';

  return [yearPart, monthPart].filter(Boolean).join(' ');
}
